import { Image } from "@/components/Image";
import { PlatformBadges } from "@/app/routes/dashboard/content-calendar/components/PlatformPicker";
import { formatDuration } from "@/app/routes/dashboard/content-calendar/calendar.utils";
import { mediaTypeHasDuration } from "@/types/contentCalendar.type";
import { useWorkshopIdea } from "../api/useWorkshopIdeas";
import WorkshopOutputCard from "./WorkshopOutputCard";
import { StateTag } from "./WorkshopParts";

type Props = {
  ideaId: string;
  /** From the tool result — shown immediately, before the fetch lands. */
  fallbackTitle: string | null;
  onOpen: (ideaId: string) => void;
};

/**
 * The concept idea a turn saved, as an output card. An idea is its own record,
 * so IDEA is a fact; the format, the brief and "on calendar" come from the idea.
 */
export default function ConceptIdeaPreviewCard({ ideaId, fallbackTitle, onOpen }: Props) {
  const { data: idea, isLoading } = useWorkshopIdea(ideaId);
  const title = idea?.title?.trim() || fallbackTitle?.trim() || "Untitled idea";
  const duration =
    idea && mediaTypeHasDuration(idea.mediaType) ? formatDuration(idea.durationSeconds) : null;
  const format = idea ? [idea.aspectRatio, duration].filter(Boolean).join(" · ") : null;
  const thumbnail = idea?.reference?.thumbnailUrl ?? null;

  return (
    <WorkshopOutputCard
      tag="IDEA"
      meta={format || null}
      title={title}
      loading={isLoading && !fallbackTitle}
      onOpen={() => onOpen(ideaId)}
      openLabel={`Open ${title}`}
      aside={
        thumbnail ? (
          <Image src={thumbnail} alt="" loading="lazy" className="ws-out-thumb" />
        ) : null
      }
    >
      {idea?.synopsis ? <p className="ws-out-line">{idea.synopsis}</p> : null}
      {idea && (idea.channels.length > 0 || idea.brief || idea.status === "MIGRATED") ? (
        <div className="ws-out-facts">
          <PlatformBadges platforms={idea.channels} />
          {idea.brief ? <StateTag>Brief v{idea.brief.version}</StateTag> : null}
          {idea.status === "MIGRATED" ? <StateTag>On calendar</StateTag> : null}
        </div>
      ) : null}
    </WorkshopOutputCard>
  );
}
